import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Stack, Typography, Button, Stepper, Step, StepLabel, Divider, Box } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

import { FormProvider, useForm } from "react-hook-form";

import { CancelButton } from "../../shared/CancelButton";
import { ProgressButton } from "../../shared/ProgressButton";

import { BulkWorkorderContext } from "./BulkWorkorderContext";
import { WorkorderDetailsForm } from "./WorkorderDetailsForm";
import { WorkorderLocationsAndWorkflows } from "./WorkorderLocationsAndWorkflows";
import { WorkorderTargetDates } from "./WorkorderTargetDates";
import { BulkWorkorderSummary } from "./BulkWorkorderSummary";
import { Customer } from "../../shared/interfaces/Customer";

interface Props {
  open: boolean;
  onClose: () => void;
  customer?: Customer;
  customerSelected?: boolean;
}

export function WorkorderBulkCreateDialog(props: Props) {
  const { t } = useTranslation();
  const { createWorkorders, workorderList, bulkCreationStatus } = useContext(BulkWorkorderContext);
  const formValidation = useForm({ mode: "onBlur" });

  const [activeStep, setActiveStep] = useState(0);

  function handleDialogClose() {
    formValidation.clearErrors();
    props.onClose();
  }

  const steps = [
    {
      label: t("workorders.workorderDetails"),
      content: <WorkorderDetailsForm customer={props.customer} customerSelected={props.customerSelected} />,
    },
    {
      label: t("workflows.locationsAndWorkflows"),
      content: <WorkorderLocationsAndWorkflows />,
    },
    {
      label: t("workorders.targetDates"),
      content: <WorkorderTargetDates />,
    },
    {
      label: t("common.summary"),
      content: <BulkWorkorderSummary />,
    },
  ];

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  async function checkStepErrors() {
    if (activeStep === 0 || activeStep === 2) {
      return true;
    } else if (activeStep === 1) {
      return await formValidation.trigger("locationsAndWorkflows");
    } else return false;
  }

  const handleNext = () => {
    checkStepErrors().then((isValid) => {
      isValid && setActiveStep((prevActiveStep) => prevActiveStep + 1);
    });
  };

  function getStepContent(step: number) {
    return steps[step]?.content;
  }

  const dialogHeader = (
    <Typography variant="h5" component="p">
      {t("workorders.bulkCreateWorkOrders")}
    </Typography>
  );
  const dialogFooter = (
    <Stack direction="row" justifyContent="flex-end" spacing={1}>
      {bulkCreationStatus === "complete" && activeStep === steps.length - 1 ? (
        <>
          <Button variant="contained" color="primary" onClick={handleDialogClose}>
            {t("common.close")}
          </Button>
        </>
      ) : (
        <>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            {t("common.back")}
          </Button>
          {activeStep === steps.length - 1 ? (
            <ProgressButton
              startIcon={<SaveIcon />}
              onClick={createWorkorders}
              disabled={bulkCreationStatus === "loading"}
              loading={bulkCreationStatus === "loading"}
            >
              {t("workorders.bulkCreateWorkorders", {
                count: workorderList.length,
              })}
            </ProgressButton>
          ) : (
            <Button variant="contained" color="primary" onClick={handleNext}>
              {t("common.next")}
            </Button>
          )}

          <CancelButton onClick={handleDialogClose} />
        </>
      )}
    </Stack>
  );

  return (
    <>
      <FormProvider {...formValidation}>
        <Dialog
          header={dialogHeader}
          footer={dialogFooter}
          visible={props.open}
          baseZIndex={undefined}
          closable={bulkCreationStatus === "pending" || bulkCreationStatus === "complete"}
          onHide={handleDialogClose}
          breakpoints={{ "1960px": "90vw", "1300px": "90vw" }}
          style={{ width: "50vw", height: "100%" }}
        >
          <Stepper activeStep={activeStep} sx={{ marginY: 2 }}>
            {steps.map((step) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Divider />
          <Box pt={2}>{getStepContent(activeStep)}</Box>
        </Dialog>
      </FormProvider>
    </>
  );
}
